import { ReactNode, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/common/components/ui/dialog';
import { ModalContext } from './ModalContext';
import { ModalOptions, ModalRenderProps } from './types';
import { useCloseOnNavigation } from '@/common/hooks/useCloseOnNavigation';
import { cn } from '@/lib/utils';

export interface ModalProviderProps {
  children: ReactNode;
}

interface ModalState<T = unknown> {
  options: ModalOptions<T>;
  renderProps: ModalRenderProps<T>;
}

const maxWidthClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const resolverRef = useRef<((value: unknown) => void) | null>(null);

  const showModal = <T,>(options: ModalOptions<T>): Promise<T | undefined> => {
    return new Promise<T | undefined>((resolve) => {
      resolverRef.current = resolve as (value: unknown) => void;

      const handleResolve = (value?: T) => {
        setIsOpen(false);
        resolverRef.current?.(value);
        resolverRef.current = null;
      };

      setModalState({
        options: options as ModalOptions<unknown>,
        renderProps: { resolve: handleResolve } as ModalRenderProps<unknown>,
      });
      setIsOpen(true);
    });
  };

  const handleNavigationClose = () => {
    // On navigation, close modal without resolving (promise remains pending)
    setIsOpen(false);
    resolverRef.current = null;
  };

  useCloseOnNavigation(handleNavigationClose);

  const maxWidth = modalState?.options.maxWidth ?? 'sm';
  const maxWidthClass = maxWidthClasses[maxWidth] ?? 'max-w-sm';

  return (
    <ModalContext value={{ showModal }}>
      {children}
      <Dialog open={isOpen} onOpenChange={(open) => !open && modalState?.renderProps.resolve()}>
        <DialogContent className={cn(maxWidthClass, 'w-full', modalState?.options.className)}>
          {modalState && modalState.options.content(modalState.renderProps)}
        </DialogContent>
      </Dialog>
    </ModalContext>
  );
};
